<template>
    <div class="card" :class="{'mobile': getMobileAppState}" @click="emit('cardClickHandler', card)">
        <div class="top-layer">
            <div class="type-wrapper">
                <component :is="iconHandler(card.operation.type)"/>
                <div class="type-description">{{ card.title }}</div>
            </div>
            <div class="reuse">
                <cycle-icon/>
            </div>
        </div>
        <div class="bottom-layer">
            <div class="description">{{ card.description }}</div>
            <div
            class="sub-description"
            v-mask="card.operation.type === 'INTERNATIONAL_TRANSFER' ? TMX_IBAN : null">
                {{ cardSubDescription }}
            </div>
        </div>
    </div>
</template>

<script setup>
import CycleIcon from '@/assets/icons/cycle.svg?inline'

/* Vue & Asset */
import { defineAsyncComponent, computed } from 'vue'
import { iconMapper } from '@/assets/scripts/mappers/frequentOperations'
import { accountFormatHelper, ibanFormat } from '@/assets/scripts/strings'
import { TMX_IBAN } from '@/assets/constants/masks'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* Emmitters */
const emit = defineEmits(['cardClickHandler'])

/* Props
@card {Object} - operation info to display on card
*/
const props = defineProps({
    card: Object
})

const { getMobileAppState } = mapGetters()

const iconHandler = type => {
    const icon = iconMapper(type)
	return defineAsyncComponent(() => import('@/assets/icons/' + icon + '.svg?inline'))
}

const cardSubDescription = computed(() => {
    if (props.card.operation.type === 'TRANSFER') {
        if (props.card.subDescription.length > 14) {
            return ibanFormat(props.card.subDescription)
        }
        return accountFormatHelper(props.card.subDescription)
    }

    return props.card.subDescription
})
</script>

<style lang="scss" scoped>
.card {
    width: calc((100% - 24px) / 4);
    height: 104px;

    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: 12px;

    padding: 8px 8px 16px 16px;

    border-radius: 16px;
    background-color: var(--dashboard-06);
    box-shadow: 0px 6px 24px rgba(0, 0, 0, 0.16);

    cursor: pointer;

    .top-layer {
        display: flex;
        flex-direction: row;
        gap: 18px;

        .type-wrapper {
            display: flex;
            flex-direction: column;
            width: calc(100% - 50px);

            svg {
                :deep(.change-color-fill) {
                    fill: var(--primary);
                }
            }

            .type-description {
                @include H6-Medium;
                line-height: 18px;

                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;

                color: var(--primary);
            }
        }

        .reuse {
            width: 32px;
            height: 32px;

            display: flex;
            align-items: center;
            justify-content: center;

            border-radius: 50%;
            background-color: var(--secondary-light-4);

            svg {
                :deep(.change-color-fill) {
                    fill: var(--secondary);
                }
            }
        }
    }

    .bottom-layer {
        .description {
            @include H7-Regular;
            line-height: 16px;

            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: var(--secondary-dark-3);
        }

        .sub-description {
            @include H7-Medium;
            line-height: 16px;

            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: var(--secondary-dark-3);
        }
    }

    &.mobile {
        width: 100%;
    }
}
</style>
